import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  approveRegistrationController,
  ApproveRegistrationModel,
  CheckBookingLimitResponseModel,
  checkRegistrationBookingLimitController,
  createRegistrationController,
  CreateRegistrationModel,
  deleteRegistrationController,
  DeleteRegistrationModel,
  getRegistrationController,
  GetResponseRegistrationModel,
  MutateResponseRegistrationModel,
  QueryKeyRegistration,
  updateRegistrationController,
  UpdateRegistrationModel,
} from "@factory/model/query-model-registration";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";

interface CheckBookingLimitPayload {
  tenant: string;
  date: string;
}

export function useQyGetRegistration(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseRegistrationModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getRegistrationController(
      search,
      limit,
      offset,
      order,
      { filter: JSON.stringify(filter) },
      dateFilter,
      startDate,
      endDate,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRegistrationModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyRegistration, search, limit, offset, order, filter],
    queryFn: () => apiFn(search, limit, offset, order, filter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRegistrationById(
  id: string,
  onSuccess?:
    | ((data: GetResponseRegistrationModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (id: string, search = "", limit = 1, offset = 0) => {
    await checkSession();

    showProgress();
    const operation = await getRegistrationController(
      search,
      limit,
      offset,
      undefined,
      { filter: JSON.stringify({ code: id }) },
      undefined,
      undefined,
      undefined,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRegistrationModel;
  };

  return useQuery({
    queryKey: [QueryKeyRegistration, id],
    queryFn: () => apiFn(id),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateRegistration(
  onSuccess?:
    | ((data: MutateResponseRegistrationModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: CreateRegistrationModel) => {
    // await checkSession();

    showProgress();
    const operation = await createRegistrationController(
      payload,
      // authHeaders()
    );
    const response = (await operation()).data;
    return response as MutateResponseRegistrationModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyRegistration);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdateRegistration(
  onSuccess?:
    | ((data: MutateResponseRegistrationModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: UpdateRegistrationModel) => {
    await checkSession();

    showProgress();
    const operation = await updateRegistrationController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["message"] as MutateResponseRegistrationModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyRegistration);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeleteRegistration(
  onSuccess?:
    | ((data: MutateResponseRegistrationModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteRegistrationModel) => {
    await checkSession();

    showProgress();
    const operation = await deleteRegistrationController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["message"] as MutateResponseRegistrationModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyRegistration);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyApproveRegistration(
  onSuccess?:
    | ((data: MutateResponseRegistrationModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: ApproveRegistrationModel) => {
    await checkSession();

    showProgress();
    const operation = await approveRegistrationController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseRegistrationModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyRegistration);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCheckBookingLimit(
  onSuccess?:
    | ((data: CheckBookingLimitResponseModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async ({ tenant, date }: CheckBookingLimitPayload) => {
    showProgress();
    const operation = await checkRegistrationBookingLimitController(
      tenant,
      date,
    );
    const response = (await operation()).data;
    return response as CheckBookingLimitResponseModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
